import { motion } from "motion/react";
import { Send } from "lucide-react";
import { TOKENS } from "../ui/dashboard-primitives";

// ── Suggestion pill ─────────────────────────────────────────────────────────
//
// Coach v3 changed what these are. They used to be coach-side suggestions
// under the coach's last message; they are now *drafts of your next message* —
// right-aligned, shaped like the user's own bubble (the 6px notch points at
// the user's avatar), and outlined rather than filled. The dashed border is
// the whole idea: a message you have not sent yet. Hover commits it to solid.
//
// Staggered entry per Emil's framework: 50ms between siblings, 220ms duration,
// strong ease-out, subtle 3px lift. Press feedback via whileTap.

interface CoachPillProps {
  children: React.ReactNode;
  onClick: () => void;
  index: number;
}

export function CoachPill({ children, onClick, index }: CoachPillProps) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 3 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.22,
        delay: index * 0.05,
        ease: [0.23, 1, 0.32, 1],
      }}
      whileTap={{ scale: 0.97 }}
      className="inline-flex items-center gap-2 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-(--accent-amber)"
      style={{
        borderRadius: "20px 6px 20px 20px",
        padding: "8px 16px",
        fontSize: "13px",
        minHeight: "40px",
        background: "rgba(28, 24, 22, 0.75)",
        border: "1px dashed " + TOKENS.amberEmphasis,
        color: TOKENS.amberLight,
        fontFamily: "var(--font-body)",
      }}
      onMouseEnter={(e) => {
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        e.currentTarget.style.background = "rgba(232, 197, 160, 0.14)";
        e.currentTarget.style.borderStyle = "solid";
        e.currentTarget.style.color = TOKENS.cream;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(28, 24, 22, 0.75)";
        e.currentTarget.style.borderStyle = "dashed";
        e.currentTarget.style.color = TOKENS.amberLight;
      }}
    >
      {children}
      <Send className="h-[13px] w-[13px] shrink-0" strokeWidth={2.2} aria-hidden />
    </motion.button>
  );
}
